import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppSelector } from '../../store/hooks';
import { selectInterventionRoomProperties } from '../../store/interventionRoomProperties/interventionRoomPropertiesSlice';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import { NEW_ROOM_ID, useRoomListEditing } from '../../hooks/useRoomListEditing';
import { useInterventionRoomProperties } from '../../hooks/useInterventionRoomProperties';
import { useInterventionRooms } from '../../hooks/useInterventionRooms';
import { useTableSort } from '../UI';
import { DeleteInterventionRoomModal } from './DeleteInterventionRoomModal';
import { getInterventionRoomColumns } from './interventionRoomColumns';
import { DEFAULT_INTERVENTION_ROOMS_SORT } from './interventionRoomsConstants';
import { RoomListView } from '../RoomListView/RoomListView';

function parsePositiveInteger(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

export const InterventionRoomsSettings = () => {
  const { t } = useTranslation();
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const locationId = locationIdParam ?? null;
  const canWrite = useHasPermission(PERMISSIONS.InterventionRoomsWrite);

  useInterventionRoomProperties();
  const allRoomProperties = useAppSelector(selectInterventionRoomProperties);
  const { rooms, createRoom, updateRoom, deleteRoom } = useInterventionRooms(locationId);

  const { sort, handleSortChange } = useTableSort(DEFAULT_INTERVENTION_ROOMS_SORT);
  const [editingCapacityLimit, setEditingCapacityLimit] = useState('');
  const [editingPriority, setEditingPriority] = useState('');
  const {
    searchQuery,
    setSearchQuery,
    filteredRooms,
    editingRoomId,
    effectiveEditingRoomId,
    editingName,
    setEditingName,
    openPickerRoomId,
    roomToDelete,
    setRoomToDelete,
    startEditing,
    startCreating,
    stopEditing,
    openPicker,
    closePicker
  } = useRoomListEditing(rooms);

  const startEditName = useCallback(
    (roomId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      setEditingCapacityLimit(room?.capacityLimit?.toString() ?? '');
      setEditingPriority(room?.priority?.toString() ?? '');
      startEditing(roomId, room?.description ?? '');
    },
    [rooms, startEditing]
  );

  const stopEditingRoom = useCallback(() => {
    stopEditing();
    setEditingCapacityLimit('');
    setEditingPriority('');
  }, [stopEditing]);

  const saveName = useCallback(
    async (roomId: string, name: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, {
        description: name,
        capacityLimit: parsePositiveInteger(editingCapacityLimit),
        priority: parsePositiveInteger(editingPriority),
        interventionRoomPropertyIds: room.interventionRoomProperties.map((rp) => rp.id)
      });
      stopEditingRoom();
    },
    [rooms, editingCapacityLimit, editingPriority, updateRoom, stopEditingRoom]
  );

  const handleCreateRoom = useCallback(
    async (name: string) => {
      await createRoom(name);
      stopEditingRoom();
    },
    [createRoom, stopEditingRoom]
  );

  const addProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, {
        description: room.description,
        capacityLimit: room.capacityLimit,
        priority: room.priority,
        interventionRoomPropertyIds: [
          ...room.interventionRoomProperties.map((rp) => rp.id),
          propertyId
        ]
      });
    },
    [rooms, updateRoom]
  );

  const removeProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, {
        description: room.description,
        capacityLimit: room.capacityLimit,
        priority: room.priority,
        interventionRoomPropertyIds: room.interventionRoomProperties
          .map((rp) => rp.id)
          .filter((id) => id !== propertyId)
      });
    },
    [rooms, updateRoom]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!roomToDelete) return;
    await deleteRoom(roomToDelete.id);
    setRoomToDelete(null);
  }, [roomToDelete, deleteRoom, setRoomToDelete]);

  const columns = useMemo(
    () =>
      getInterventionRoomColumns(t, {
        editingRoomId: effectiveEditingRoomId,
        editingName,
        editingCapacityLimit,
        editingPriority,
        openPickerRoomId,
        allRoomProperties,
        onStartEditName: startEditName,
        onNameChange: setEditingName,
        onCapacityLimitChange: setEditingCapacityLimit,
        onPriorityChange: setEditingPriority,
        onSaveName: () => {
          if (!effectiveEditingRoomId || effectiveEditingRoomId === NEW_ROOM_ID) return;
          const trimmed = editingName.trim();
          if (trimmed.length === 0) return;
          void saveName(effectiveEditingRoomId, trimmed);
        },
        onCancelEdit: stopEditingRoom,
        onOpenPicker: openPicker,
        onClosePicker: closePicker,
        onAddProperty: (roomId, propertyId) => {
          void addProperty(roomId, propertyId);
        },
        onRemoveProperty: (roomId, propertyId) => {
          void removeProperty(roomId, propertyId);
        },
        onDelete: (room) => setRoomToDelete(room),
        canWrite
      }),
    [
      t,
      effectiveEditingRoomId,
      editingName,
      editingCapacityLimit,
      editingPriority,
      openPickerRoomId,
      allRoomProperties,
      startEditName,
      saveName,
      stopEditingRoom,
      setEditingName,
      setRoomToDelete,
      openPicker,
      closePicker,
      addProperty,
      removeProperty,
      canWrite
    ]
  );

  return (
    <RoomListView
      title={t('settings-intervention-rooms-header')}
      searchLabel={t('settings-search-intervention-room')}
      searchPlaceholder={t('intervention-rooms-search-placeholder')}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      columns={columns}
      rooms={filteredRooms}
      getRowKey={(room) => room.id}
      sort={sort}
      onSortChange={handleSortChange}
      canWrite={canWrite}
      addLabel={t('add-intervention-room')}
      isAddingRoom={editingRoomId === NEW_ROOM_ID}
      onStartAdd={startCreating}
      onSaveNewRoom={(name) => void handleCreateRoom(name)}
      onCancelAdd={stopEditingRoom}
    >
      {canWrite && roomToDelete && (
        <DeleteInterventionRoomModal
          room={roomToDelete}
          onClose={() => setRoomToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </RoomListView>
  );
};
